import { Gamepad2, Swords, Map as MapIcon, HeartHandshake, HelpCircle, Trophy, ShoppingBag, Dumbbell, User, Users, LayoutGrid, BookOpen } from "lucide-react";
import type { PageKey } from "../../appNavTypes";
import type { MessageKey } from "../../i18n/englishCatalog";

/** External Tradeport marketplace URL for the Suikamon collection. */
export const MARKETPLACE_URL =
  "https://www.tradeport.xyz/sui/collection/0x1df382f1d4ffecbf4e2ceaadca021a68b353bb502011e5a1c6bed2979b4be186%3A%3Asuikamon%3A%3ASuikamon?bottomTab=trades&tab=items";

/**
 * Game-shell navigation model.
 *
 * The "game" routes render inside the persistent GameShell: a left side-rail on desktop
 * (logo → nav items → player profile) and a compact bottom tab bar on mobile. Marketing/
 * content/legal routes keep the standard web top-nav.
 *
 * Navigation gates on the authenticated identity (useWalletConnection), never on a Sui
 * wallet — see CLAUDE.md identity-vs-custody rules.
 */

/** A single entry in the game navigation. */
export interface GameNavTab {
  key: string;
  /** Catalog key of the visible label (translated where it renders). */
  label: MessageKey;
  /** Catalog key of the screen-reader label. */
  ariaLabel: MessageKey;
  Icon: typeof Gamepad2;
  /** Route the entry navigates to. */
  path: string;
  /** Page keys that render this entry in its active state. */
  activePages: PageKey[];
  /** Whether this entry appears in the compact mobile bottom tab bar. */
  mobile: boolean;
  /** When set, the entry opens this external URL in a new tab instead of navigating in-app. */
  externalUrl?: string;
  /** Notification-dot source for this entry: "pet-care" (a Suikamon needs care) or
   *  "daily-rival" (an unplayed campaign Rival of the Day). */
  notify?: "pet-care" | "daily-rival";
  /** Secondary (less important) entry — rendered smaller/compact (icon + text on one line). */
  secondary?: boolean;
  /**
   * Desktop: not a rail entry — it lives in the ☰ trainer menu (GameTrainerMenu) so the rail
   * fits a normal screen without a scrollbar. The mobile "More" sheet still lists it.
   */
  inTrainerMenu?: boolean;
  /**
   * Web2: kept (route and page still exist) but not shown in any menu. Adopt — one free starter,
   * the rest are found in eggs; Store — it sells skills for ONE Suikamon, so it opens from that
   * Suikamon's Skills page.
   */
  hiddenFromMenu?: boolean;
  /** When set, clicking the entry opens a small popup of these sub-options instead of
   *  navigating (used by Battle → Training / Solo / Squad). */
  submenu?: GameNavSubItem[];
}

/** A sub-option inside a nav entry's popup (e.g. Battle → Training). */
export interface GameNavSubItem {
  key: string;
  label: MessageKey;
  description: MessageKey;
  path: string;
  Icon: typeof Gamepad2;
}

/** The battle hub landing route — the neutral entry point for the Battle tab. */
export const BATTLE_HUB_PATH = "/pvp-ranked";

/** Training battles — the one battle mode that needs no account and no wallet. */
export const BATTLE_TRAINING_PATH = "/pvp-trial";

/**
 * The Battle Lab — preset fighters against wild Suikamon, so it needs no Suikamon of your own.
 * Where the Battle button takes a visitor who has nothing to train yet: the fastest way from
 * "Battle" to an actual battle is one that does not first ask them to adopt and hatch a pet.
 */
export const BATTLE_LAB_PATH = "/pvp-lab";

/**
 * Page keys that render inside the GameShell (get the rail / bottom bar and hide the desktop
 * top nav). Adopt is included so the launcher persists through onboarding; Help/News are
 * intentionally excluded so they navigate back out to the website chrome.
 */
export const GAME_PAGES: readonly PageKey[] = [
  "play",
  "campaign",
  "adopt",
  "pvp-lab",
  "pvp-trial",
  "pvp-ranked",
  "pvp-squad-ranked",
  // The first two are reached from a button on the pet's own screen and neither carries a nav
  // of its own, so without the shell they are one-way doors: a player who taps 🃏 or 🎁 lands
  // on a website-chrome page with no rail, no tab bar and no back link. Keeping the shell IS
  // the way back. They stay out of GAME_NAV_TABS deliberately — cards belong to a pet, and a
  // rail entry cannot know which pet you mean.
  "pet-cards",
  "daily-rewards",
  // The store DOES have a rail entry (it picks its own shopper), and is listed here for the
  // other half of the deal: the shell it renders inside.
  "store",
] as const;

/** True when the given page renders inside the game shell. */
export function isGamePage(page: PageKey): boolean {
  return GAME_PAGES.includes(page);
}

/**
 * Ordered navigation entries. Desktop rail shows them all; the mobile bottom bar shows only
 * those flagged `mobile` (Help/News stay in the mobile top-nav menu to keep the bar thumb-
 * sized). Home = the pet home. Collection was removed per product direction.
 */
export const GAME_NAV_TABS: readonly GameNavTab[] = [
  {
    key: "play",
    label: "navigation.tab.play",
    ariaLabel: "navigation.tab.playAria",
    Icon: Gamepad2,
    path: "/play",
    activePages: ["play"],
    mobile: true,
    notify: "pet-care",
  },
  {
    key: "campaign",
    label: "navigation.tab.campaign",
    ariaLabel: "navigation.tab.campaign",
    Icon: MapIcon,
    path: "/campaign",
    activePages: ["campaign"],
    mobile: true,
    notify: "daily-rival",
  },
  {
    key: "battle",
    label: "navigation.tab.battle",
    ariaLabel: "navigation.tab.battle",
    Icon: Swords,
    path: BATTLE_HUB_PATH,
    activePages: ["pvp-lab", "pvp-trial", "pvp-ranked", "pvp-squad-ranked"],
    mobile: true,
    submenu: [
      { key: "training", label: "navigation.tab.battleTraining", description: "navigation.tab.battleTrainingDesc", path: "/pvp-trial", Icon: Dumbbell },
      { key: "solo", label: "navigation.tab.battleSolo", description: "navigation.tab.battleSoloDesc", path: "/pvp-ranked", Icon: User },
      { key: "squad", label: "navigation.tab.battleSquad", description: "navigation.tab.battleSquadDesc", path: "/pvp-squad-ranked", Icon: Users },
    ],
  },
  {
    // The Skill Store is a first-class destination, not a sub-screen: Suika is earned by CARE
    // (the thing the Suikamon tab is about) and spent here, so the two ends of that loop both
    // need to be one tap away — which is why it earns a slot in the mobile bar.
    key: "store",
    label: "navigation.tab.store",
    ariaLabel: "navigation.tab.storeAria",
    Icon: ShoppingBag,
    path: "/store",
    activePages: ["store"],
    mobile: true,
    hiddenFromMenu: true,
  },
  {
    key: "adopt",
    label: "navigation.tab.adopt",
    ariaLabel: "navigation.tab.adoptAria",
    Icon: HeartHandshake,
    path: "/adopt",
    activePages: ["adopt"],
    mobile: false,
    secondary: true,
    hiddenFromMenu: true,
  },
  {
    key: "collection",
    label: "navigation.tab.collection",
    ariaLabel: "navigation.tab.collection",
    Icon: LayoutGrid,
    path: "/collection",
    activePages: ["collection"],
    mobile: false,
    secondary: true,
  },
  {
    key: "rankings",
    label: "navigation.tab.rankings",
    ariaLabel: "navigation.tab.rankings",
    Icon: Trophy,
    path: "/rankings",
    activePages: ["rankings", "trainers"],
    mobile: false,
    secondary: true,
  },
  {
    key: "story",
    label: "navigation.tab.story",
    ariaLabel: "navigation.tab.story",
    Icon: BookOpen,
    path: "/story",
    activePages: ["story"],
    mobile: false,
    secondary: true,
    inTrainerMenu: true,
  },
  // Web2 hard reset: no NFTs, so no external marketplace entry (MARKETPLACE_URL is parked).
  {
    key: "help",
    label: "navigation.tab.help",
    ariaLabel: "navigation.tab.help",
    Icon: HelpCircle,
    path: "/help",
    activePages: ["help"],
    mobile: false,
    secondary: true,
    inTrainerMenu: true,
  },
] as const;

/**
 * The Battle entry as this visitor should see it.
 *
 * Signed-in players get the full Training / Solo / Squad chooser. Everyone else gets a
 * single tap straight into Training, because Solo and Squad are ranked ladders that need an
 * account: offering them to a free player puts a sign-up wall exactly where the player asked
 * for a battle. Training needs neither an account nor a Suikamon — it hands a visitor with
 * nothing the starter pick — so it is the honest destination for the Battle button until
 * there is an identity behind it.
 *
 * `canPlayRanked` is the authenticated identity (`useWalletConnection().isConnected`), never
 * a Sui wallet — see CLAUDE.md "gate on IDENTITY".
 */
export function battleTabFor(tab: GameNavTab, canPlayRanked: boolean): GameNavTab {
  if (canPlayRanked || !tab.submenu) return tab;
  return { ...tab, path: BATTLE_TRAINING_PATH, submenu: undefined };
}
